import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Collapse,
  Paper,
  Popper
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import OverflowTooltip from '../../shared/OverflowTooltip';
import { FILTER_VALUES, SCRUM_BACKLOG_SENTINEL, SCRUM_SPRINT_STATES } from '../../shared/appConstants';
import { groupScrumSprintsByState, normalizeSprintIds } from '../../shared/scrumSprintUtils';

const STATE_LABELS = {
  [SCRUM_SPRINT_STATES.ACTIVE]: 'Active',
  [SCRUM_SPRINT_STATES.DRAFT]: 'Draft',
  [SCRUM_SPRINT_STATES.COMPLETED]: 'Completed'
};

const ScrumSprintStateSelect = ({ sprints = [], value, onChange, allLabel = 'All Scrum Sprints' }) => {
  const anchorRef = useRef(null);
  const groups = useMemo(() => groupScrumSprintsByState(sprints), [sprints]);
  const [open, setOpen] = useState(false);
  const [draftIds, setDraftIds] = useState([]);
  const [draftBacklog, setDraftBacklog] = useState(false);
  const [expanded, setExpanded] = useState({});

  // O(1) membership test against the current draft selection
  const draftSet = useMemo(() => new Set(draftIds.map(String)), [draftIds]);

  const availableIds = useMemo(() => sprints.map((s) => String(s.id)), [sprints]);

  // Current applied selection as a clean string[] (no "all", no nulls)
  const appliedIds = useMemo(() => normalizeSprintIds(value, FILTER_VALUES.ALL).filter((id) => id !== SCRUM_BACKLOG_SENTINEL), [value]);
  const backlogSelected = useMemo(() => (Array.isArray(value) ? value : String(value || '').split(',')).includes(SCRUM_BACKLOG_SENTINEL), [value]);

  // O(1) membership test for applied sprint IDs
  const selectedSprintSet = useMemo(() => new Set(appliedIds), [appliedIds]);

  // One chip per state type that has at least 1 sprint selected
  const selectedStateGroups = useMemo(() => {
    if (!appliedIds.length) return [];
    return groups.filter((g) => g.sprints.some((s) => selectedSprintSet.has(String(s.id))));
  }, [appliedIds, groups, selectedSprintSet]);

  const beginSelection = () => {
    setDraftIds(appliedIds.length || backlogSelected ? [...appliedIds] : [...availableIds]);
    setDraftBacklog(backlogSelected || (appliedIds.length === 0 && !backlogSelected));
    setExpanded({});
    setOpen(true);
  };

  const cancelSelection = () => {
    setOpen(false);
    setDraftIds([]);
  };

  const applySelection = () => {
    const allSelected = draftBacklog && availableIds.every((id) => draftSet.has(String(id)));
    const nextValue = allSelected ? FILTER_VALUES.ALL : [...draftIds, ...(draftBacklog ? [SCRUM_BACKLOG_SENTINEL] : [])].join(',');
    onChange({ target: { value: nextValue } });
    setOpen(false);
  };

  const toggleIds = (ids) => {
    const allSelected = ids.every((id) => draftSet.has(String(id)));
    setDraftIds((current) => {
      const currentSet = new Set(current.map(String));
      return allSelected
        ? current.filter((sel) => !ids.some((id) => String(id) === String(sel)))
        : [...current, ...ids.filter((id) => !currentSet.has(String(id)))];
    });
  };

  const toggleExpand = (e, state) => {
    e.stopPropagation();
    setExpanded((prev) => prev[state] ? {} : { [state]: true });
  };

  return (
    <>
      <div
        ref={anchorRef}
        onClick={() => (open ? cancelSelection() : beginSelection())}
        className={`MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-formControl MuiInputBase-sizeSmall${open ? ' Mui-focused' : ''}`}
        style={{ cursor: 'pointer', width: '100%', position: 'relative' }}
      >
        <div className="MuiSelect-select MuiSelect-outlined MuiInputBase-input MuiOutlinedInput-input MuiInputBase-inputSizeSmall scrum-sprint-chip-row">
          {selectedStateGroups.length > 0 || backlogSelected
            ? <>
              {backlogSelected && <Box component="span" className="scrum-sprint-chip scrum-sprint-chip--backlog">Backlog</Box>}
              {selectedStateGroups.map((g) => (
                <Box
                  key={g.state}
                  component="span"
                  className={`scrum-sprint-chip scrum-sprint-chip--${g.state.toLowerCase()}`}
                >
                  {STATE_LABELS[g.state]}
                </Box>
              ))}
            </>
            : <Box component="span" className="filter-selected-value-text">{allLabel}</Box>
          }
        </div>
        <fieldset
          aria-hidden="true"
          className="MuiOutlinedInput-notchedOutline"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            top: 0,
            margin: 0,
            padding: 0,
            pointerEvents: 'none',
            borderRadius: 'inherit',
            borderStyle: 'solid',
            borderWidth: '1px'
          }}
        >
          <legend style={{ display: 'none' }} />
        </fieldset>
      </div>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        className="scrum-sprint-popper"
        modifiers={[
          { name: 'offset', options: { offset: [0, 5] } },
          { name: 'flip', enabled: true },
          { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport', padding: 12 } },
          {
            name: 'sameWidth',
            enabled: true,
            phase: 'beforeWrite',
            requires: ['computeStyles'],
            fn: ({ state }) => { state.styles.popper.width = `${state.rects.reference.width}px`; },
            effect: ({ state }) => { state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`; }
          }
        ]}
      >
        <ClickAwayListener onClickAway={cancelSelection}>
          <Paper elevation={8} className="scrum-sprint-popper-menu">
            <Box className="scrum-sprint-popper-menu__groups">
              <div className="scrum-sprint-group scrum-sprint-group--backlog">
                <div className="scrum-sprint-state-heading scrum-sprint-state-heading--backlog" onClick={() => setDraftBacklog((selected) => !selected)}>
                  <Checkbox checked={draftBacklog} size="small" onClick={(e) => e.stopPropagation()} onChange={() => setDraftBacklog((selected) => !selected)} sx={{ padding: '2px 6px 2px 0', color: 'var(--scrum-sprint-checkbox-color)', '&.Mui-checked': { color: 'var(--scrum-sprint-checkbox-color)' } }} />
                  <Box component="span" className="scrum-sprint-state-heading__label">Backlog</Box>
                </div>
              </div>
              {groups.map((group) => {
                const stateKey = group.state.toLowerCase();
                const groupIds = group.sprints.map((s) => String(s.id));
                const selectedCount = groupIds.filter((id) => draftSet.has(id)).length;
                const allGroupSelected = groupIds.length > 0 && selectedCount === groupIds.length;
                const isExpanded = Boolean(expanded[group.state]);

                return (
                  <div key={group.state} className={`scrum-sprint-group scrum-sprint-group--${stateKey}`}>
                    <div
                      className={`scrum-sprint-state-heading scrum-sprint-state-heading--${stateKey}`}
                      onClick={() => toggleIds(groupIds)}
                    >
                      <Checkbox
                        checked={allGroupSelected}
                        indeterminate={selectedCount > 0 && !allGroupSelected}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleIds(groupIds)}
                        sx={{ padding: '2px 6px 2px 0', color: 'var(--scrum-sprint-checkbox-color)', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'var(--scrum-sprint-checkbox-color)' } }}
                      />
                      <Box component="span" className="scrum-sprint-state-heading__label">
                        {STATE_LABELS[group.state] || group.state}
                      </Box>
                      {selectedCount > 0 && (
                        <Box component="span" className="scrum-sprint-state-heading__count">
                          {selectedCount} Selected
                        </Box>
                      )}
                      <Box
                        component="span"
                        className="scrum-sprint-state-heading__toggle"
                        onClick={(e) => toggleExpand(e, group.state)}
                      >
                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </Box>
                    </div>

                    <Collapse in={isExpanded} unmountOnExit>
                      <div className="scrum-sprint-rows-scroll">
                        {group.sprints.map((sprint) => (
                          <div
                            key={sprint.id}
                            className={`scrum-sprint-individual-option scrum-sprint-individual-option--${stateKey}${draftSet.has(String(sprint.id)) ? ' scrum-sprint-individual-option--selected' : ''}`}
                            onClick={() => toggleIds([String(sprint.id)])}
                          >
                            <Checkbox
                              checked={draftSet.has(String(sprint.id))}
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleIds([String(sprint.id)])}
                              sx={{ padding: '2px 6px 2px 0', color: 'var(--scrum-sprint-checkbox-color)', '&.Mui-checked': { color: 'var(--scrum-sprint-checkbox-color)' } }}
                            />
                            <OverflowTooltip
                              title={sprint?.name || 'Unnamed Sprint'}
                              tooltipWidth={350}
                              className="scrum-sprint-individual-option__name"
                              placement="top-start"
                            />
                          </div>
                        ))}
                      </div>
                    </Collapse>
                  </div>
                );
              })}
            </Box>

            <Box className="scrum-sprint-popper-menu__actions">
              <Button variant="outlined" size="small" onClick={cancelSelection}>Cancel</Button>
              <Button variant="contained" size="small" onClick={applySelection}>Apply</Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

ScrumSprintStateSelect.propTypes = {
  sprints: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  allLabel: PropTypes.string
};

export default ScrumSprintStateSelect;
